import React, { useEffect } from "react";
import { Button, Modal, ModalBody, Spinner } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import SimpleBar from "simplebar-react";
import { StudyFilterFormValues } from "./StudyFilter";
import { filterSection } from "../helpers/studyconstant";
import "../../../Volunteer/MasterVolunteer/css/VolunteerFilterModal.scss";



/* =========================================================
   Props
 ========================================================= */

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onApply: (filters: StudyFilterFormValues) => void;
  onClear: () => void;
  existingFilters?: StudyFilterFormValues | null;
}

/* =========================================================
   Component
 ========================================================= */

const StudyFilterModal: React.FC<Props> = ({
  isOpen,
  toggle,
  onApply,
  onClear,
  existingFilters,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<StudyFilterFormValues>({
    defaultValues: existingFilters ?? {
      studytitle: "",
      studycode: "",
      sponsorname: null,
      studydesign: null,
      totalsubjects: null,
      numberofperiods: null,
    },
  });

  /* ---------------- APPLY FILTER ---------------- */

  const onSubmit = (values: StudyFilterFormValues) => {
    onApply(values);
    toggle();
  };

  /* ---------------- CLEAR FILTER ---------------- */

  const handleClear = () => {
    onClear();
    toggle();
    reset({
      studytitle: "",
      studycode: "",
      sponsorname: null,
      studydesign: null,
      totalsubjects: null,
      numberofperiods: null,
    });
  };

  useEffect(() => {
    if (existingFilters) {
      reset(existingFilters);
    } else {
      reset({
        studytitle: "",
        studycode: "",
        sponsorname: null,
        studydesign: null,
        totalsubjects: null,
        numberofperiods: null,
      });
    }
  }, [existingFilters, reset, isOpen]);

  /* ---------------- REACT SELECT STYLES ---------------- */
  const customSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "#f3f3f3",
      borderColor: state.isFocused ? "#405189" : "transparent",
      borderRadius: "4px",
      fontSize: "12px",
      minHeight: "27px",
      height: "auto",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#405189",
      },
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: "0 6px",
      fontSize: "12px",
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "#495057",
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "#adb5bd",
    }),
    input: (base: any) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (base: any) => ({
      ...base,
      height: "25px",
    }),
    option: (base: any, state: any) => ({
      ...base,
      fontSize: "12px",
      padding: "6px 10px",
      minHeight: "25px",
      display: "flex",
      alignItems: "center",
    }),
    menu: (base: any) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  /* ---------------- RENDER ---------------- */

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      centered
      backdrop="static"
      className="study-filter-modal common_modal"
    >
      <div className="modal-header">
        <h5 className="modal-title-custom">Filter Study</h5>
        <p className="modal-subtitle">
          Apply specific criteria to narrow down your study selection
        </p>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={toggle}
        ></button>
      </div>

      <ModalBody className="p-0">
        <SimpleBar className="p-4" style={{ maxHeight: "450px" }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {filterSection.map((section, sIdx) => (
              <div key={sIdx} className={`filter-section ${sIdx === filterSection.length - 1 ? "mb-0" : ""}`}>
                <h6 className="section-title">{section.title}</h6>
                <div className="row">
                  {section.fields.map((field) => (
                    <div key={field.name} className="col-md-6 mb-3">
                      <div className="form-group mb-0">
                        <label>{field.label}</label>
                        <Controller
                          name={field.name}
                          control={control}
                          render={({ field: { value, onChange, ...fieldProps } }) => {
                            if (field.type === "select") {
                              return (
                                <Select
                                  {...fieldProps}
                                  options={field.options}
                                  styles={customSelectStyles}
                                  placeholder={field.placeholder}
                                  isClearable
                                  value={
                                    field.options?.find((opt) => opt.value === value) || null
                                  }
                                  onChange={(val: any) => onChange(val ? val.value : null)}
                                />
                              );
                            }
                            return (
                              <input
                                {...fieldProps}
                                type={field.type}
                                className="form-control"
                                placeholder={field.placeholder}
                                value={value === null ? "" : value}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  onChange(field.type === "number" ? (val === "" ? null : Number(val)) : val);
                                }}
                              />
                            );
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </form>
        </SimpleBar>
      </ModalBody>

      <div className="modal-footer-custom">
        <button type="button" className="btn-clear" onClick={handleClear}>
          <i className="ri-refresh-line"></i> Clear Filters
        </button>

        <div className="d-flex gap-2">
          <Button color="secondary" outline onClick={toggle} >
            Cancel
          </Button>

          <Button
            disabled={isSubmitting}
            onClick={handleSubmit(onSubmit)}
            outline
            color="primary"
          >
            {isSubmitting && <Spinner size="sm" className="me-2" />}
            Apply Filters
          </Button>
        </div>
      </div>
    </Modal >
  );
};

export default StudyFilterModal;
